/**
 * `426 app_update_required`: ai_engine's iOS build floor (`app/client_floor.py`) answers it only to
 * a request whose `X-Primepoint-Client` names an iOS build below the floor. It is not an access
 * decision (no `X-Access-Admission`), so nothing here touches access state or offline files: the
 * page hands it to the host's one Update Primepoint screen (`Platform.appUpdate`, drawn natively).
 * `client_update_required` stays the access protocol's own 426 and is unaffected.
 */
import type { AppUpdateNotice, Platform } from "../platform/Platform";
import { getPlatform } from "../platform/platformRef";

export const APP_UPDATE_REQUIRED = "app_update_required";

function noticeFrom(body: unknown): AppUpdateNotice {
    const detail = (body as { detail?: unknown } | null | undefined)?.detail;
    const field = (name: string): string | null => {
        const value = (detail as Record<string, unknown> | null | undefined)?.[name];
        return typeof value === "string" ? value : null;
    };
    return { minBuild: field("min_build"), storeUrl: field("store_url") };
}

/** Forwards a floored answer to the host's update screen. Reads only the status, the error-code
 *  header and (when the caller has it) the parsed body; returns whether it was one. */
export function forwardAppUpdate(
    status: number | undefined,
    header: (name: string) => unknown,
    body?: unknown,
    host: Pick<Platform, "appUpdate"> = getPlatform(),
): boolean {
    if (status !== 426 || header("x-primepoint-error-code") !== APP_UPDATE_REQUIRED) return false;
    host.appUpdate?.required(noticeFrom(body));
    return true;
}
